import React, { useEffect, useState, useCallback, useRef,useMemo } from "react";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
    Box,
    Typography,
    Button,
    Paper,
    TextField,
    Autocomplete,
    Grid,
    Switch,
    FormControlLabel,
    IconButton,
    Chip,
    Divider,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Checkbox,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Alert,
} from "@mui/material";
import BlockIcon from "@mui/icons-material/Block";
import RestoreIcon from "@mui/icons-material/Restore";
import EditIcon from "@mui/icons-material/Edit";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { authHeader, clearToken } from "../../auth";
import FisStatusMini from "./FisStatusMini";
import TimingEventSubNav from "./TimingEventSubNav";
import TelemarkRanking from "./TelemarkRanking";
import PrintSortManager from "./PrintSortManager";
import { groupBySections } from "../../utils/printSort";
// PDF "design officiel" de la page Course & exports (même rendu partout).
// ⚠️ Adapter le chemin si le dossier de racePdf.js est différent.
import { runResultsPdf, eventResultsPdf, toEventLive, enrichLive, telemarkToLive, telemarkEventLive, analysisPdf } from "../timingsoftware/racePdf";
import { officialStartlistPdf } from "../../utils/officialStartlist";
import { brandHeader, brandFooter, ownLogo, ensureOwnLogo } from "../../utils/pdfBranding";

const POLL_INTERVAL_MS = 2000;

// Codes de discipline télémark (page Course & exports / panneau FIS) —
// repli pour détecter une course télémark si le secteur n'est pas
// renseigné (configs créées avant le 01.10.2026).
const TELEMARK_DISCIPLINES = ["SP", "CL", "PSP", "PS", "PSQ"];

// Largeur commune à TOUTES les colonnes (Prochains au départ, En attente —
// Inter N/Arrivée, Départ/Inter N/Arrivée impulsions brutes) — pour que les
// deux rangées s'alignent verticalement colonne par colonne. Ajouté le
// 26.09.2026 (auparavant : md=4 variable, 300px et 280px selon la rangée,
// donc désalignées).
// Grille à 4 colonnes ÉGALES, divisant la largeur DISPONIBLE de l'écran
// (pas des largeurs fixes en pixels) — un canal = une colonne. Remplace
// les largeurs fixes du 26.09.2026 (300/450px), jugées insuffisantes sur
// grand écran. Toute colonne au-delà de 4 (ex: 3 intermédiaires + arrivée
// = 5 colonnes) passe naturellement à la ligne suivante, elle aussi
// divisée en 4.
const GRID_COLUMNS = "repeat(4, 1fr)";

function extractRawTime(rawValue) {
    if (!rawValue) return null;
    const match = String(rawValue).match(/(\d{2}:\d{2}:\d{2})\.(\d+)/);
    if (!match) return null;
    const [, hms, frac] = match;
    return `${hms}.${frac.slice(0, 4)}`;
}

function formatTime(iso, rawValue) {
    const raw = extractRawTime(rawValue);
    if (raw) return raw;
    if (!iso) return "—";
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-CH", { hour12: false, hour: "2-digit", minute: "2-digit", second: "2-digit" }) +
        "." + String(d.getMilliseconds()).padStart(3, "0").slice(0, 2);
}

function formatElapsed(ms) {
    if (ms == null) return "—";
    if (ms < 0) return "⚠ incohérent";
    // Tronqué au 1/100 (règle FIS), jamais arrondi — 01.10.2026, même
    // affichage que les temps envoyés à la FIS.
    const totalCentis = Math.floor(ms / 10);
    const minutes = Math.floor(totalCentis / 6000);
    const seconds = Math.floor((totalCentis % 6000) / 100);
    const centis = totalCentis % 100;
    return `${minutes}:${String(seconds).padStart(2, "0")}.${String(centis).padStart(2, "0")}`;
}

export default function TimingChronoPage() {
    const { eventKey } = useParams();
    const [searchParams, setSearchParams] = useSearchParams();
    const navigate = useNavigate();

    const [configured, setConfigured] = useState(null);
    const [showSetup, setShowSetup] = useState(false);
    const [knownSources, setKnownSources] = useState([]);

    const [startChrono, setStartChrono] = useState("");
    const [startChannel, setStartChannel] = useState("");
    const [finishChrono, setFinishChrono] = useState("");
    const [finishChannel, setFinishChannel] = useState("");

    const [intermediates, setIntermediates] = useState([]);
    const [interEnabled, setInterEnabled] = useState([true, true, true]);

    const [startEnabled, setStartEnabled] = useState(true);
    const [finishEnabled, setFinishEnabled] = useState(true);

    const [liveData, setLiveData] = useState({ start: [], finish: [], intermediates: [], ranking: [] });
    const [startlist, setStartlist] = useState([]);
    const [error, setError] = useState("");
    const [advancingRun, setAdvancingRun] = useState(false);

    // Course télémark ? — défini à la création de la course (secteur TM,
    // page Course & exports). Ajouté le 01.10.2026 : affiche alors le
    // classement avec temps ski / pénalités / temps manche
    // (TelemarkRanking.jsx).
    const [isTelemark, setIsTelemark] = useState(false);
    // Config de la course (Course & exports) : en-tête des PDF officiels et
    // « Tri impression » (config.printSort).
    const [raceConfig, setRaceConfig] = useState(null);

    // Fenêtre "À traiter avant de clôturer" — maintenant une fenêtre à
    // ouvrir sur demande plutôt qu'un bloc toujours affiché.
    const [showUnresolvedDialog, setShowUnresolvedDialog] = useState(false);

    // Ferme la fenêtre "À traiter" en forçant D'ABORD le blur de tout
    // champ actif (numéro de porte, dossard...) — sinon un clic direct sur
    // "Fermer" (ou en dehors) juste après avoir tapé pouvait démonter la
    // fenêtre avant que l'onBlur n'ait eu l'occasion de lancer la
    // sauvegarde, faisant perdre la saisie en cours.
    const closeUnresolvedDialog = () => {
        if (document.activeElement && document.activeElement !== document.body) {
            document.activeElement.blur();
        }
        setShowUnresolvedDialog(false);
    };

    // Fenêtre "Report by the Referee" (FIS) — voir renderRefereeReport.
    const [showRefereeReport, setShowRefereeReport] = useState(false);
    const [refereeInfoLoaded, setRefereeInfoLoaded] = useState(false);
    const [refereeInfo, setRefereeInfo] = useState({
        place: "",
        country: "",
        codex: "",
        eventName: "",
        date: "",
        category: "",
        gender: "",
        event: "",
        timePublished: "",
        deadline: "",
        refereeName: "",
    });

    const [now, setNow] = useState(Date.now());
    const pollRef = useRef(null);

    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: { "Content-Type": "application/json", ...authHeader(), ...(options.headers || {}) },
            });
            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }
            return res;
        },
        [navigate]
    );

    const switchViewRun = (runNumber) => {
        const next = new URLSearchParams(searchParams);
        next.set("run", runNumber);
        setSearchParams(next, { replace: true });
    };

    useEffect(() => {
        apiFetch(`/api/timing/config/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((cfg) => {
                if (cfg) {
                    setStartChrono(cfg.startChrono || "");
                    setStartChannel(cfg.startChannel || "");
                    setFinishChrono(cfg.finishChrono || "");
                    setFinishChannel(cfg.finishChannel || "");
                    setIntermediates(
                        (cfg.intermediates || []).map((it) => ({
                            chronoId: it.chronoId || "",
                            channel: it.channel || "",
                            distance: it.distance ?? "",
                        }))
                    );
                    setConfigured(true);
                } else {
                    setConfigured(false);
                    setShowSetup(true);
                }
            });

        apiFetch("/api/timing/known-sources")
            .then((res) => (res && res.ok ? res.json() : []))
            .then((data) => setKnownSources(data || []));

        // Secteur de la course (Course & exports) : TM = télémark.
        apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`)
            .then((res) => (res && res.ok ? res.json() : null))
            .then((cfg) => {
                setRaceConfig(cfg);
                setIsTelemark(cfg?.sector === "TM" || TELEMARK_DISCIPLINES.includes(cfg?.discipline));
            })
            .catch(() => setIsTelemark(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [eventKey]);

    const loadLive = useCallback(async () => {
        const run = searchParams.get("run");
        const url = `/api/timing/live/${encodeURIComponent(eventKey)}${run ? `?run=${run}` : ""}`;
        const res = await apiFetch(url);
        if (!res) return;
        if (res.ok) {
            const data = await res.json();
            setLiveData(data);
            setError("");
        }
    }, [apiFetch, eventKey, searchParams]);

    const loadStartlist = useCallback(async () => {
        const run = searchParams.get("run");
        const url = `/api/timing/startlist/${encodeURIComponent(eventKey)}${run ? `?run=${run}` : ""}`;
        const res = await apiFetch(url);
        if (!res) return;
        if (res.ok) setStartlist(await res.json());
    }, [apiFetch, eventKey, searchParams]);

    const advanceRun = async () => {
        const current = liveData.currentRun || 1;
        if (unresolvedBibs.length > 0) {
            const proceed = window.confirm(
                `${unresolvedBibs.length} dossard(s) n'ont ni temps d'arrivée ni statut (voir "À traiter") — ` +
                `ils ne seront pas classés pour la manche ${current}. Passer quand même à la manche ${current + 1} ?`
            );
            if (!proceed) return;
        }
        setAdvancingRun(true);
        try {
            // Coupe le live FIS AVANT de changer de manche. Le /stop
            // (timingFisRouter.js) flush les toutes dernières données de la
            // manche qu'on quitte avant de couper la connexion — appelé
            // APRÈS /advance, il flusherait la manche SUIVANTE (encore
            // vide) au lieu de clôturer proprement celle qu'on vient de
            // terminer, d'où cet ordre précis. Best-effort : si le live FIS
            // n'était pas démarré, /stop ne fait rien ; si l'appel échoue
            // (ex: serveur FIS injoignable), ça ne doit pas bloquer le
            // passage à la manche suivante.
            try {
                await apiFetch(`/api/timing/fis/${encodeURIComponent(eventKey)}/stop`, { method: "POST" });
            } catch {
                /* best-effort */
            }

            const res = await apiFetch(`/api/timing/run/${encodeURIComponent(eventKey)}/advance`, { method: "POST" });
            if (res && res.ok) {
                switchViewRun(current + 1);
            } else if (res) {
                const data = await res.json().catch(() => ({}));
                setError(data.error || `HTTP ${res.status}`);
            }
        } finally {
            setAdvancingRun(false);
        }
    };

    const unresolvedBibs = useMemo(() => {
        const classifiedBibs = new Set((liveData.ranking || []).map((r) => String(r.bib)));
        const statusedBibs = new Set(Object.keys(liveData.statuses || {}));
        return startlist.filter((a) => a.bib && !classifiedBibs.has(String(a.bib)) && !statusedBibs.has(String(a.bib)));
    }, [startlist, liveData.ranking, liveData.statuses]);

    const alreadyStatusBibs = useMemo(() => liveData.notClassified || [], [liveData.notClassified]);
    const unresolvedTotalCount = unresolvedBibs.length + alreadyStatusBibs.length;

    useEffect(() => {
        if (!configured) return;
        loadLive();
        loadStartlist();
        pollRef.current = setInterval(() => {
            loadLive();
            loadStartlist();
        }, POLL_INTERVAL_MS);
        return () => clearInterval(pollRef.current);
    }, [configured, loadLive, loadStartlist]);

    useEffect(() => {
        if (!configured) return;
        const t = setInterval(() => setNow(Date.now()), 100);
        return () => clearInterval(t);
    }, [configured]);

    const saveConfig = async (force = false) => {
        if (!startChrono || !startChannel || !finishChrono || !finishChannel) {
            setError("Le canal de départ et le canal d'arrivée sont obligatoires.");
            return;
        }

        const payload = {
            startChrono,
            startChannel,
            finishChrono,
            finishChannel,
            intermediates: intermediates.map((it) => ({
                chronoId: it.chronoId,
                channel: it.channel,
                distance: it.distance === "" || it.distance == null ? null : Number(it.distance),
            })),
        };
        if (force) payload.force = true;

        const res = await apiFetch(`/api/timing/config/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(payload),
        });
        if (!res) return;

        if (res.status === 409) {
            // Conflit avec un AUTRE event actif (pas archivé, sinon il
            // aurait déjà été exclu côté serveur) — cas légitime si le
            // même poste de chronométrage sert plusieurs events. On
            // confirme explicitement plutôt que de forcer silencieusement.
            const data = await res.json().catch(() => ({}));
            const otherKeys = [...new Set((data.conflicts || []).map((c) => c.eventKey))];
            if (
                window.confirm(
                    `Ces canaux sont déjà utilisés par : ${otherKeys.join(", ")}.\n\n` +
                    `Si c'est le même poste de chronométrage réutilisé pour cet event, cliquez OK pour ` +
                    `confirmer — ${otherKeys.join(", ")} cessera de recevoir ces impulsions.`
                )
            ) {
                await saveConfig(true);
            }
            return;
        }

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }

        setError("");
        setConfigured(true);
        setShowSetup(false);
    };

    const addIntermediate = () => {
        if (intermediates.length >= 3) return;
        setIntermediates((prev) => [...prev, { chronoId: "", channel: "", distance: "" }]);
    };
    const updateIntermediate = (index, field, value) => {
        setIntermediates((prev) => prev.map((it, i) => (i === index ? { ...it, [field]: value } : it)));
    };
    const removeIntermediate = (index) => {
        setIntermediates((prev) => prev.filter((_, i) => i !== index));
    };

    const toggleCancel = async (id, cancel) => {
        const res = await apiFetch(`/api/timing/impulses/${encodeURIComponent(eventKey)}/${id}/cancel`, {
            method: "POST",
            body: JSON.stringify({ cancel }),
        });
        if (res && res.ok) loadLive();
    };

    const addManualImpulse = async (column) => {
        const labels = { start: "départ", finish: "arrivée", inter1: "intermédiaire 1", inter2: "intermédiaire 2", inter3: "intermédiaire 3" };
        const bib = window.prompt(`Dossard (${labels[column] || column} manuel) :`);
        if (bib === null) return;

        const res = await apiFetch(`/api/timing/impulses/manual/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify({ column, bib: bib.trim() || null }),
        });
        if (res && res.ok) loadLive();
        else if (res) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
        }
    };

    // Attribue (ou efface) un statut DNS/DNF/DSQ/DSQP à un dossard.
    // `gateNumber` (numéro de porte) n'est pertinent que pour DSQ — voir
    // renderStatusSelect et le Report by the Referee.
    const setBibStatus = async (bib, status, gateNumber) => {
        const run = liveData.runNumber || 1;
        const res = await apiFetch(
            `/api/timing/status/${encodeURIComponent(eventKey)}/${encodeURIComponent(bib)}?run=${run}`,
            { method: "POST", body: JSON.stringify({ status: status || null, gateNumber: gateNumber || null }) }
        );
        if (res && res.ok) loadLive();
    };

    const saveImpulseBib = async (id, value) => {
        const res = await apiFetch(
            `/api/timing/impulses/${encodeURIComponent(eventKey)}/${id}/bib`,
            { method: "POST", body: JSON.stringify({ bib: value }) }
        );
        if (res && res.ok) loadLive();
    };

    const saveImpulseTimestamp = async (id, value) => {
        const res = await apiFetch(
            `/api/timing/impulses/${encodeURIComponent(eventKey)}/${id}/timestamp`,
            { method: "POST", body: JSON.stringify({ time: value }) }
        );
        if (res && res.ok) {
            loadLive();
        } else if (res) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
        }
    };

    const chronoIdOptions = [...new Set(knownSources.map((s) => s.chrono_id))];
    const channelOptionsFor = (chronoId) =>
        [...new Set(knownSources.filter((s) => s.chrono_id === chronoId).map((s) => s.channel))];

    const startedBibs = new Set(
        liveData.start.filter((s) => !s.cancelled && s.bib).map((s) => String(s.bib))
    );
    const notYetStarted = startlist.filter(
        (a) => !startedBibs.has(String(a.bib)) && liveData.statuses?.[String(a.bib)] !== "DNS"
    );
    const next5 = notYetStarted.slice(0, 5);

    const saveStartlistOrder = async (list) => {
        const withOrder = list.map((a, i) => ({ ...a, order: i + 1 }));
        setStartlist(withOrder);
        await apiFetch(`/api/timing/startlist/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(withOrder),
        });
    };

    const saveStartlistBib = async (target, enteredBib) => {
        const currentHolder = startlist.find(
            (a) => a !== target && String(a.bib) === String(enteredBib)
        );

        let updated;
        if (currentHolder) {
            const targetIndex = startlist.indexOf(target);
            const holderIndex = startlist.indexOf(currentHolder);
            updated = [...startlist];
            [updated[targetIndex], updated[holderIndex]] = [updated[holderIndex], updated[targetIndex]];
        } else {
            updated = startlist.map((a) => (a === target ? { ...a, bib: enteredBib } : a));
        }

        await saveStartlistOrder(updated);
    };

    const moveNext5 = (index, direction) => {
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= next5.length) return;

        const itemA = next5[index];
        const itemB = next5[targetIndex];
        const realIndexA = startlist.indexOf(itemA);
        const realIndexB = startlist.indexOf(itemB);
        if (realIndexA === -1 || realIndexB === -1) return;

        const updated = [...startlist];
        [updated[realIndexA], updated[realIndexB]] = [updated[realIndexB], updated[realIndexA]];
        saveStartlistOrder(updated);
    };

    // Files d'attente par étape (inter1/inter2/inter3/arrivée) — ajouté le
    // 06.09.2026, remplace l'ancien panneau unique "Suivi en course".
    // Même pattern que saveStartlistOrder/saveStartlistBib/moveNext5 pour
    // "Prochains au départ", mais appliqué à liveData.stageQueues (fourni
    // par computeLiveTiming) et persisté via /api/timing/stage-queue.
    const saveStageQueueOrder = async (stageKey, bibs) => {
        setLiveData((prev) => ({
            ...prev,
            stageQueues: (prev.stageQueues || []).map((sq) =>
                sq.key !== stageKey
                    ? sq
                    : {
                        ...sq,
                        waiting: bibs.map(
                            (bib) => sq.waiting.find((w) => String(w.bib) === String(bib)) || { bib, name: null, sinceTime: null }
                        ),
                    }
            ),
        }));
        await apiFetch(
            `/api/timing/stage-queue/${encodeURIComponent(eventKey)}/${stageKey}?run=${liveData.runNumber || 1}`,
            { method: "POST", body: JSON.stringify(bibs) }
        );
    };

    const moveStageQueueItem = (stageKey, index, direction) => {
        const sq = (liveData.stageQueues || []).find((s) => s.key === stageKey);
        if (!sq) return;
        const targetIndex = index + direction;
        if (targetIndex < 0 || targetIndex >= sq.waiting.length) return;
        const bibs = sq.waiting.map((w) => w.bib);
        [bibs[index], bibs[targetIndex]] = [bibs[targetIndex], bibs[index]];
        saveStageQueueOrder(stageKey, bibs);
    };

    const editStageQueueBib = (stageKey, index, enteredBib) => {
        const sq = (liveData.stageQueues || []).find((s) => s.key === stageKey);
        if (!sq) return;
        const bibs = sq.waiting.map((w) => w.bib);
        // Comme pour la liste de départ : si le dossard tapé occupe déjà
        // une autre position de CETTE MÊME file, on échange les deux
        // plutôt que de créer un doublon.
        const existingIndex = bibs.findIndex((b, i) => i !== index && String(b) === String(enteredBib));
        if (existingIndex !== -1) {
            [bibs[index], bibs[existingIndex]] = [bibs[existingIndex], bibs[index]];
        } else {
            bibs[index] = enteredBib;
        }
        saveStageQueueOrder(stageKey, bibs);
    };

    // Menu de statut — un champ "Porte n°" apparaît dès que DSQ est
    // sélectionné (voir Report by the Referee).
    function renderStatusSelect(bib, allowedStatuses = ["DNF", "DSQ", "DSQP"]) {
        const current = liveData.statuses?.[bib] || "";
        const currentGate = liveData.notClassified?.find((n) => String(n.bib) === String(bib))?.gateNumber || "";
        const options =
            current && !allowedStatuses.includes(current) ? [...allowedStatuses, current] : allowedStatuses;
        return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <FormControl size="small" sx={{ minWidth: 90 }}>
                    <Select
                        value={current}
                        displayEmpty
                        onChange={(e) => setBibStatus(bib, e.target.value, currentGate)}
                    >
                        <MenuItem value="">Statut —</MenuItem>
                        {options.map((s) => (
                            <MenuItem key={s} value={s}>
                                {s}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
                {(current === "DSQ" || current === "DSQP") && (
                    <TextField
                        size="small"
                        placeholder="Porte n°"
                        defaultValue={currentGate}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") e.target.blur();
                        }}
                        onBlur={(e) => {
                            const value = e.target.value.trim();
                            if (value !== currentGate) setBibStatus(bib, current, value);
                        }}
                        sx={{ width: 90 }}
                    />
                )}
            </Box>
        );
    }

    function renderImpulseColumn(title, impulses, enabled, setEnabled, column) {
        return (
            <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                    <Typography variant="h6">{title}</Typography>
                    <FormControlLabel
                        control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} size="small" />}
                        label={enabled ? "Actif" : "Désactivé"}
                    />
                </Box>
                <Button
                    size="small"
                    variant="outlined"
                    fullWidth
                    sx={{ mb: 1 }}
                    onClick={() => addManualImpulse(column)}
                >
                    + Ajouter manuellement (M)
                </Button>

                {!enabled ? (
                    <Typography color="text.secondary" variant="body2">
                        Canal désactivé — impulsions masquées (toujours enregistrées côté serveur).
                    </Typography>
                ) : impulses.length === 0 ? (
                    <Typography color="text.secondary" variant="body2">
                        Aucune impulsion reçue.
                    </Typography>
                ) : (
                    <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                        {impulses.map((imp) => (
                            <Box
                                key={imp.id}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    py: 0.75,
                                    px: 1,
                                    mb: 0.5,
                                    borderRadius: 1,
                                    backgroundColor: imp.cancelled ? "#ffebee" : "#f5f5f5",
                                    opacity: imp.cancelled ? 0.6 : 1,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <TextField
                                        key={imp.id}
                                        size="small"
                                        defaultValue={imp.bib ?? ""}
                                        placeholder="?"
                                        disabled={imp.cancelled}
                                        onKeyDown={(e) => {
                                            if (e.key === "Enter") {
                                                e.target.blur();
                                            }
                                        }}
                                        onBlur={(e) => {
                                            const value = e.target.value;
                                            if (value !== String(imp.bib ?? "")) saveImpulseBib(imp.id, value);
                                        }}
                                        sx={{
                                            width: 80,
                                            "& input": {
                                                fontWeight: "bold",
                                                fontSize: "1.1rem",
                                                textDecoration: imp.cancelled ? "line-through" : "none",
                                            },
                                        }}
                                    />
                                    {imp.manual && !imp.cancelled && (
                                        <Chip label="M" size="small" color="secondary" sx={{ height: 18, fontSize: "0.65rem" }} />
                                    )}
                                    {imp.auto && !imp.cancelled && (
                                        <Chip label="auto" size="small" variant="outlined" sx={{ height: 18, fontSize: "0.65rem" }} />
                                    )}
                                    {imp.manual && !imp.cancelled ? (
                                        <TextField
                                            key={`time-${imp.id}`}
                                            size="small"
                                            variant="standard"
                                            defaultValue={formatTime(imp.receivedAt, imp.rawValue)}
                                            onKeyDown={(e) => {
                                                if (e.key === "Enter") e.target.blur();
                                            }}
                                            onBlur={(e) => {
                                                const value = e.target.value.trim();
                                                const current = formatTime(imp.receivedAt, imp.rawValue);
                                                if (value && value !== current) saveImpulseTimestamp(imp.id, value);
                                            }}
                                            InputProps={{ disableUnderline: true }}
                                            sx={{ width: 110, "& input": { fontSize: "0.85rem", color: "text.secondary" } }}
                                        />
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            {formatTime(imp.receivedAt, imp.rawValue)}
                                        </Typography>
                                    )}
                                </Box>

                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    {imp.bib && !imp.cancelled && renderStatusSelect(imp.bib)}
                                    <IconButton
                                        size="small"
                                        color={imp.cancelled ? "success" : "error"}
                                        onClick={() => toggleCancel(imp.id, !imp.cancelled)}
                                        title={imp.cancelled ? "Réactiver" : "Annuler cette impulsion"}
                                    >
                                        {imp.cancelled ? <RestoreIcon fontSize="small" /> : <BlockIcon fontSize="small" />}
                                    </IconButton>
                                </Box>
                            </Box>
                        ))}
                    </Box>
                )}
            </Paper>
        );
    }

    // Report by the Referee (FIS) — réplique la mise en page du
    // formulaire officiel (voir PDF fourni). DSQP signifie "disqualifié
    // POTENTIELLEMENT" (statut provisoire, en attente de décision du
    // jury) — pas "Not permitted to start" comme supposé initialement.
    // C'est précisément l'objet de ce rapport : signaler ces cas au
    // jury. DSQ et DSQP rejoignent donc le MÊME tableau nominatif, DSQP
    // marqué "Provisoire" en Notes pour rester distinguable. La case
    // "Not permitted to start" du formulaire papier reste vide (aucun
    // statut de l'appli ne correspond à cette catégorie précise) — à
    // remplir à la main si besoin, comme Gate Judge.
    const dsqList = (liveData.notClassified || []).filter((n) => n.status === "DSQ" || n.status === "DSQP");
    const dnsList = (liveData.notClassified || []).filter((n) => n.status === "DNS");
    const dnfList = (liveData.notClassified || []).filter((n) => n.status === "DNF");

    function renderNumberGrid(list) {
        const cells = list.map((n) => n.bib);
        while (cells.length % 12 !== 0) cells.push("");
        const rows = [];
        for (let i = 0; i < cells.length; i += 12) rows.push(cells.slice(i, i + 12));
        if (rows.length === 0) rows.push(Array(12).fill(""));
        return (
            <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 4 }}>
                <tbody>
                {rows.map((row, ri) => (
                    <tr key={ri}>
                        {row.map((c, ci) => (
                            <td
                                key={ci}
                                style={{
                                    border: "1px solid #000",
                                    width: `${100 / 12}%`,
                                    height: 24,
                                    textAlign: "center",
                                    fontSize: 12,
                                }}
                            >
                                {c}
                            </td>
                        ))}
                    </tr>
                ))}
                </tbody>
            </table>
        );
    }

    // Partage / export groupé — 07.10.2026 : PDF "design officiel" (mêmes
    // documents que Course & exports), avec le « Tri impression » de la
    // course, plusieurs fichiers d'un coup. Mobile : partage natif (WhatsApp
    // avec les PDF joints) ; ordinateur : téléchargement des PDF + WhatsApp
    // Web avec le texte (joindre les fichiers à la main).
    const [shareOpen, setShareOpen] = useState(false);
    const [shareSel, setShareSel] = useState({ startlist: false, run: true, event: true, referee: false, text: false });
    const [shareSorted, setShareSorted] = useState(true);
    const [shareSheet, setShareSheet] = useState(true);
    const [shareBusy, setShareBusy] = useState(false);
    const [shareError, setShareError] = useState("");
    // « Tri impression » accessible depuis la fenêtre de partage (07.10.2026).
    const [showPrintSort, setShowPrintSort] = useState(false);

    // Report by the Referee en PDF "design officiel" — 01.10.2026.
    // Remplace l'impression navigateur (qui débordait de la page) : même
    // contenu et même mise en page que le formulaire FIS (cases à traits
    // noirs), calibré pour tenir sur UNE page A4. Les tableaux s'allongent
    // seulement s'il y a beaucoup de disqualifiés / DNS / DNF.
    function buildRefereeReportPdf() {
        const doc = new jsPDF({ unit: "mm", format: "a4" });
        const M = 14;
        const W = 210 - 2 * M;
        const grid = {
            theme: "grid",
            margin: { left: M, right: M, bottom: 20 },
            styles: { fontSize: 9, textColor: 0, lineColor: 0, lineWidth: 0.25, cellPadding: 1.5, valign: "middle" },
            headStyles: { fillColor: [235, 235, 235], textColor: 0, fontStyle: "bold", fontSize: 8 },
        };
        const v = (x) => (x == null || x === "" ? " " : String(x));

        doc.setFont(undefined, "bold");
        doc.setFontSize(16);
        doc.text("REPORT BY THE REFEREE", M, 18);
        doc.setFont(undefined, "normal");
        let y = 23;

        // Identification de la course : libellé (gris, petit) puis valeur.
        const label = (t, colSpan = 1) => ({ content: t, colSpan, styles: { fontSize: 7.5, fontStyle: "bold", fillColor: [235, 235, 235], cellPadding: 1 } });
        const value = (t, colSpan = 1) => ({ content: v(t), colSpan, styles: { fontSize: 10, minCellHeight: 8 } });
        autoTable(doc, {
            ...grid,
            startY: y,
            columnStyles: { 0: { cellWidth: W * 0.4 }, 1: { cellWidth: W * 0.35 }, 2: { cellWidth: W * 0.25 } },
            body: [
                [label("Place"), label("Country"), label("Codex")],
                [value(refereeInfo.place), value(refereeInfo.country), value(refereeInfo.codex)],
                [label("Name of the Event", 2), label("Date")],
                [value(refereeInfo.eventName, 2), value(refereeInfo.date)],
                [label("Category"), label("Gender"), label("Event")],
                [value(refereeInfo.category), value(refereeInfo.gender), value(refereeInfo.event)],
            ],
        });
        y = doc.lastAutoTable.finalY + 7;

        // Disqualifiés : au moins 5 lignes (à compléter à la main si besoin).
        doc.setFont(undefined, "bold");
        doc.setFontSize(10);
        doc.text("The following competitors have been disqualified according to ICR:", M, y);
        doc.setFont(undefined, "normal");
        const dsqRows = dsqList.map((d) => [
            v(d.bib),
            v(d.name),
            v(d.nation),
            v(d.gateNumber),
            " ",
            d.status === "DSQP" ? "Provisional" : " ",
        ]);
        while (dsqRows.length < 5) dsqRows.push([" ", " ", " ", " ", " ", " "]);
        autoTable(doc, {
            ...grid,
            startY: y + 2,
            head: [["No.", "Name - Surname", "Nat.", "Gate Number", "Gate Judge", "Notes"]],
            body: dsqRows,
            bodyStyles: { minCellHeight: 7 },
            columnStyles: {
                0: { cellWidth: 16, halign: "center" },
                1: { cellWidth: 58 },
                2: { cellWidth: 16, halign: "center" },
                3: { cellWidth: 24, halign: "center" },
                4: { cellWidth: 34 },
                5: { cellWidth: W - 148 },
            },
        });
        y = doc.lastAutoTable.finalY + 7;

        // Grilles de 12 cases (n° de dossard), 2 lignes minimum.
        const numberGrid = (title, list) => {
            doc.setFont(undefined, "bold");
            doc.setFontSize(10);
            doc.text(title, M, y);
            doc.setFont(undefined, "normal");
            const cells = list.map((n) => String(n.bib));
            while (cells.length < 24 || cells.length % 12 !== 0) cells.push(" ");
            const rows = [];
            for (let i = 0; i < cells.length; i += 12) rows.push(cells.slice(i, i + 12));
            autoTable(doc, {
                ...grid,
                startY: y + 2,
                body: rows,
                styles: { ...grid.styles, halign: "center", fontSize: 10, minCellHeight: 7 },
                columnStyles: Object.fromEntries(Array.from({ length: 12 }, (_, i) => [i, { cellWidth: W / 12 }])),
            });
            y = doc.lastAutoTable.finalY + 7;
        };
        numberGrid("Did not start (N°)", dnsList);
        numberGrid("Not permitted to start (N°)", []);
        numberGrid("Did not finish (N°)", dnfList);

        // Publication + signature de l'arbitre.
        autoTable(doc, {
            ...grid,
            startY: y,
            columnStyles: Object.fromEntries([0, 1, 2, 3].map((i) => [i, { cellWidth: W / 4 }])),
            body: [
                [label("Time published"), label("Deadline"), label("Date"), label("The Referee")],
                [
                    { content: v(refereeInfo.timePublished), styles: { fontSize: 10, minCellHeight: 14 } },
                    { content: v(refereeInfo.deadline), styles: { fontSize: 10, minCellHeight: 14 } },
                    { content: v(refereeInfo.date), styles: { fontSize: 10, minCellHeight: 14 } },
                    { content: v(refereeInfo.refereeName), styles: { fontSize: 10, minCellHeight: 14, valign: "top" } },
                ],
            ],
        });

        // Pagination (normalement 1/1).
        const n = doc.getNumberOfPages();
        for (let i = 1; i <= n; i++) {
            doc.setPage(i);
            doc.setFontSize(7);
            doc.text(`${eventKey} — Page ${i}/${n}`, 210 - M, 290, { align: "right" });
        }
        // Logo 321-timing en haut à droite + pied de page commun (sans
        // bandeau, pour rester sur une page) — 04.11.2026.
        const brand = {
            logoRight: ownLogo(),
            timing: raceConfig?.timing || "321-timing.ch",
            dataprocessing: raceConfig?.dataprocessing || "321-timing.ch",
        };
        for (let i = 1; i <= doc.getNumberOfPages(); i++) {
            doc.setPage(i);
            brandHeader(doc, brand);
        }
        brandFooter(doc, brand);
        return doc;
    }

    const downloadRefereeReport = () => {
        buildRefereeReportPdf().save(`report-referee-${eventKey}.pdf`);
    };

    // Ouvre le PDF dans un nouvel onglet avec la boîte d'impression.
    const printRefereeReport = () => {
        const doc = buildRefereeReportPdf();
        doc.autoPrint();
        window.open(doc.output("bloburl"), "_blank");
    };

    const resultsText = () => {
        const runLabel = (liveData.runCount || 1) > 1 ? `Manche ${liveData.runNumber || 1}` : "Résultats";
        const MAX_ROWS = 15;
        const lines = [`🏆 ${raceConfig?.event || eventKey} — ${runLabel}`, ""];
        (liveData.ranking || []).slice(0, MAX_ROWS).forEach((r) => lines.push(`${r.rank}. [${r.bib}] ${r.name || "—"} — ${formatElapsed(r.elapsedMs)}`));
        if ((liveData.ranking || []).length > MAX_ROWS) lines.push(`… (+${liveData.ranking.length - MAX_ROWS})`);
        if (liveData.combinedRanking?.length) {
            lines.push("", "🏆 Total :");
            liveData.combinedRanking.slice(0, MAX_ROWS).forEach((r) => lines.push(`${r.rank}. [${r.bib}] ${r.name || "—"} — ${formatElapsed(r.totalMs)}`));
        }
        return lines.join("\n");
    };

    const shareSelected = async () => {
        await ensureOwnLogo();
        setShareError("");
        setShareBusy(true);
        try {
            const key = encodeURIComponent(eventKey);
            const config = { ...(raceConfig || {}), sector: raceConfig?.sector || (isTelemark ? "TM" : raceConfig?.sector) };
            const run = liveData.runNumber || 1;
            const common = { config, eventKey, groupByCategory: shareSorted, includeSheet: shareSheet, save: false };
            const getJson = async (url) => {
                const r = await apiFetch(url);
                if (!r || !r.ok) throw new Error(`lecture impossible (${url})`);
                return r.json();
            };
            // Participants : complètent nom / année / club… dans les résultats.
            const athletes = await getJson(`/api/timing/athletes/${key}`).catch(() => []);
            const files = [];
            if (shareSel.startlist) {
                const sl = await getJson(`/api/timing/startlist/${key}?run=${run}`);
                // Même PDF que la page Liste de départ.
                if (sl.length)
                    files.push(
                        officialStartlistPdf({ config, eventKey, runNumber: run, startlist: sl, includeRaceInfo: shareSheet, sorted: shareSorted, save: false })
                    );
            }
            // Télémark : résultats avec pénalités (temps de ski + style + saut).
            const isTM = isTelemark;
            if (shareSel.run) {
                const src = isTM
                    ? telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=${run}&view=run`))
                    : await getJson(`/api/timing/live/${key}?run=${run}`);
                const live = enrichLive(src, athletes);
                files.push(runResultsPdf({ ...common, run, live }));
            }
            if (shareSel.event) {
                const live = enrichLive(await getJson(`/api/timing/live/${key}`), athletes);
                let run1NotClassified = null;
                let eventLive = live;
                if ((live.runCount || 1) > 1) {
                    const run1 = enrichLive(await getJson(`/api/timing/live/${key}?run=1`), athletes);
                    run1NotClassified = run1?.notClassified || [];
                    eventLive = toEventLive(live, [run1]);
                }
                if (isTM) {
                    const lastRun = live.runCount || 1;
                    const tm =
                        lastRun > 1
                            ? await telemarkEventLive((u) => getJson(u).catch(() => null), key, lastRun, live.combineMethod)
                            : telemarkToLive(await getJson(`/api/timing/results-telemark/${key}?run=1&view=run`));
                    if (tm) eventLive = enrichLive({ ...tm, runCount: lastRun, notClassified: live.notClassified }, athletes);
                }
                files.push(eventResultsPdf({ ...common, live: eventLive, run1NotClassified, pointsByBib: null }));
            }
            if (shareSel.analysis) {
                const lv = await getJson(`/api/timing/live/${key}?run=${run}`);
                let src = lv;
                if (isTM) {
                    const tmA = await getJson(`/api/timing/results-telemark/${key}?run=${run}&view=run`).catch(() => null);
                    if (tmA) src = { ...tmA, notClassified: lv.notClassified };
                }
                files.push(analysisPdf({ config, eventKey, run, live: enrichLive(src, athletes), save: false }));
            }
            if (shareSel.referee) {
                const doc = buildRefereeReportPdf();
                files.push(new File([doc.output("blob")], `report-referee-${eventKey}.pdf`, { type: "application/pdf" }));
            }
            const caption = `🏆 ${raceConfig?.event || eventKey}`;
            const text = shareSel.text ? resultsText() : caption;

            if (!files.length) {
                window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
                setShareOpen(false);
                return;
            }
            // Mobile : un seul partage avec tous les fichiers.
            if (navigator.canShare && navigator.canShare({ files })) {
                try {
                    await navigator.share({ files, text, title: caption });
                    setShareOpen(false);
                    return;
                } catch (err) {
                    if (err?.name === "AbortError") return;
                }
            }
            // Ordinateur : téléchargement de chaque PDF, puis WhatsApp Web.
            files.forEach((f, i) =>
                setTimeout(() => {
                    const url = URL.createObjectURL(f);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = f.name;
                    a.click();
                    setTimeout(() => URL.revokeObjectURL(url), 2000);
                }, i * 400)
            );
            window.open(
                `https://wa.me/?text=${encodeURIComponent(`${text}\n\n📎 ${files.length} PDF téléchargé(s) — à joindre dans WhatsApp.`)}`,
                "_blank",
                "noopener,noreferrer"
            );
            setShareOpen(false);
        } catch (err) {
            setShareError(err.message);
        } finally {
            setShareBusy(false);
        }
    };

    // Ouvre le rapport et préremplit en combinant DEUX sources déjà
    // centralisées : Course & exports (/api/timing/swissski-config/...)
    // et le panneau FIS (/api/timing/fis-config/..., raceinfo). UNE SEULE
    // fois par ouverture pour le réseau, l'heure à chaque ouverture (sans
    // écraser une valeur déjà éditée).
    const openRefereeReport = async () => {
        setShowRefereeReport(true);

        const pad2 = (n) => String(n).padStart(2, "0");
        const nowDate = new Date();
        const deadlineDate = new Date(nowDate.getTime() + 15 * 60000);
        const timeStr = `${pad2(nowDate.getHours())}:${pad2(nowDate.getMinutes())}`;
        const deadlineStr = `${pad2(deadlineDate.getHours())}:${pad2(deadlineDate.getMinutes())}`;
        const dateStr = `${pad2(nowDate.getDate())}.${pad2(nowDate.getMonth() + 1)}.${nowDate.getFullYear()}`;

        setRefereeInfo((prev) => ({
            ...prev,
            timePublished: prev.timePublished || timeStr,
            deadline: prev.deadline || deadlineStr,
            date: prev.date || dateStr,
        }));

        // Le préremplissage RÉSEAU (Place/Codex/Category/...) reste
        // limité à une fois par event — pas besoin de re-taper à chaque
        // ouverture, et ça évite d'écraser une correction manuelle.
        if (refereeInfoLoaded) return;
        setRefereeInfoLoaded(true);

        try {
            const [swissRes, fisRes] = await Promise.all([
                apiFetch(`/api/timing/swissski-config/${encodeURIComponent(eventKey)}`),
                apiFetch(`/api/timing/fis-config/${encodeURIComponent(eventKey)}`),
            ]);
            const swiss = swissRes && swissRes.ok ? await swissRes.json() : null;
            const fis = fisRes && fisRes.ok ? await fisRes.json() : null;
            const raceinfo = fis?.raceinfo || {};

            setRefereeInfo((prev) => ({
                ...prev,
                place: prev.place || swiss?.place || "",
                codex: prev.codex || swiss?.codex || "",
                eventName: prev.eventName || swiss?.event || "",
                // La date de course (swiss.date) prime sur la date du jour
                // déjà posée ci-dessus, si elle est renseignée.
                date: swiss?.date || prev.date,
                event: prev.event || raceinfo.discipline || swiss?.discipline || "",
                category: prev.category || raceinfo.category || "",
                gender: prev.gender || raceinfo.gender || "",
                refereeName: prev.refereeName || swiss?.jury?.referee || "",
            }));
        } catch {
            /* préremplissage best-effort — un échec ici n'empêche pas de remplir le rapport à la main */
        }
    };

    // Remet le verrou à zéro si on change d'event sans recharger la page
    // (navigation côté client) — sinon le préremplissage réseau resterait
    // bloqué sur les données du PRÉCÉDENT event consulté.
    useEffect(() => {
        setRefereeInfoLoaded(false);
    }, [eventKey]);

    // Saisie du Report by the Referee à l'écran ; l'impression passe par le
    // PDF "design officiel" (buildRefereeReportPdf), qui tient sur une page.
    function renderRefereeReport() {
        const setField = (field) => (e) => setRefereeInfo((prev) => ({ ...prev, [field]: e.target.value }));
        const cellStyle = { border: "1px solid #000", padding: "3px 6px", fontSize: 12, verticalAlign: "top" };
        const labelStyle = { ...cellStyle, fontWeight: "bold" };

        const fieldBlock = (label, field) => (
            <>
                {label}
                <TextField size="small" fullWidth value={refereeInfo[field]} onChange={setField(field)} sx={{ mt: 0.5 }} />
            </>
        );

        return (
            <Box maxWidth={900}>
                <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mb: 2 }}>
                    <Button variant="outlined" onClick={() => setShowRefereeReport(false)}>
                        Fermer
                    </Button>
                    <Button variant="outlined" onClick={downloadRefereeReport}>
                        PDF (design officiel)
                    </Button>
                    <Button variant="contained" onClick={printRefereeReport}>
                        Imprimer
                    </Button>
                </Box>

                <Box sx={{ p: 2, bgcolor: "white", color: "black" }}>
                    <Typography sx={{ fontWeight: "bold", fontSize: 20, mb: 2 }}>REPORT BY THE REFEREE</Typography>

                    <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 8 }}>
                        <tbody>
                        <tr>
                            <td style={{ ...labelStyle, width: "40%" }}>{fieldBlock("Place", "place")}</td>
                            <td style={{ ...labelStyle, width: "35%" }}>{fieldBlock("Country", "country")}</td>
                            <td style={{ ...labelStyle, width: "25%" }}>{fieldBlock("Codex", "codex")}</td>
                        </tr>
                        <tr>
                            <td style={labelStyle} colSpan={2}>
                                {fieldBlock("Name of the Event", "eventName")}
                            </td>
                            <td style={labelStyle}>{fieldBlock("Date", "date")}</td>
                        </tr>
                        <tr>
                            <td style={labelStyle}>{fieldBlock("Category", "category")}</td>
                            <td style={labelStyle}>{fieldBlock("Gender", "gender")}</td>
                            <td style={labelStyle}>{fieldBlock("Event", "event")}</td>
                        </tr>
                        </tbody>
                    </table>

                    <Typography sx={{ fontWeight: "bold", fontSize: 13, mb: 0.5 }}>
                        The following competitors have been disqualified according to ICR:
                    </Typography>
                    <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 12 }}>
                        <thead>
                        <tr>
                            <th style={labelStyle}>No.</th>
                            <th style={labelStyle}>Name - Surname</th>
                            <th style={labelStyle}>Nat.</th>
                            <th style={labelStyle}>Gate Number</th>
                            <th style={labelStyle}>Gate Judge</th>
                            <th style={labelStyle}>Notes</th>
                        </tr>
                        </thead>
                        <tbody>
                        {(dsqList.length ? dsqList : [null, null, null, null, null]).map((d, i) => (
                            <tr key={i}>
                                <td style={cellStyle}>{d?.bib || ""}</td>
                                <td style={cellStyle}>{d?.name || ""}</td>
                                <td style={cellStyle}>{d?.nation || ""}</td>
                                <td style={cellStyle}>{d?.gateNumber || ""}</td>
                                <td style={cellStyle}></td>
                                <td style={cellStyle}>{d?.status === "DSQP" ? "Provisional" : ""}</td>
                            </tr>
                        ))}
                        </tbody>
                    </table>

                    <Typography sx={{ fontWeight: "bold", fontSize: 13, mb: 0.5 }}>Did not start (N°)</Typography>
                    {renderNumberGrid(dnsList)}

                    <Typography sx={{ fontWeight: "bold", fontSize: 13, mb: 0.5, mt: 1 }}>
                        Not permitted to start (N°)
                    </Typography>
                    {renderNumberGrid([])}

                    <Typography sx={{ fontWeight: "bold", fontSize: 13, mb: 0.5, mt: 1 }}>Did not finish (N°)</Typography>
                    {renderNumberGrid(dnfList)}

                    <table style={{ borderCollapse: "collapse", width: "100%", marginTop: 12 }}>
                        <tbody>
                        <tr>
                            <td style={labelStyle}>{fieldBlock("Time published", "timePublished")}</td>
                            <td style={labelStyle}>{fieldBlock("Deadline", "deadline")}</td>
                            <td style={labelStyle}>{fieldBlock("Date", "date")}</td>
                            <td style={labelStyle}>{fieldBlock("The Referee", "refereeName")}</td>
                        </tr>
                        </tbody>
                    </table>
                </Box>
            </Box>
        );
    }

    if (configured === null) {
        return (
            <Box p={3}>
                <Typography color="text.secondary">Chargement…</Typography>
            </Box>
        );
    }

    if (showRefereeReport) {
        return <Box p={3}>{renderRefereeReport()}</Box>;
    }

    if (showSetup) {
        return (
            <Box p={3} maxWidth={700}>
                <Typography variant="h4" mb={1}>
                    Configuration chrono — {eventKey}
                </Typography>
                <Typography variant="body2" color="text.secondary" mb={1}>
                    Choisis le chrono et le canal pour le départ et pour l'arrivée — les deux sont
                    obligatoires. Jusqu'à 3 intermédiaires optionnels, positionnés entre les deux.
                </Typography>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 3 }}>
                    Ces canaux sont ceux du poste de chronométrage physique (ordinateur + chronomètres
                    câblés) — ils s'appliquent identiquement à TOUTES les manches de cet event (1, 2, 3),
                    ce n'est pas la manche qui les détermine. Les manches se distinguent par une fenêtre
                    temporelle, pas par des canaux différents.
                </Typography>

                {error && (
                    <Typography color="error" sx={{ mb: 2 }}>
                        {error}
                    </Typography>
                )}

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" mb={2}>
                        🟢 Départ (obligatoire)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Autocomplete
                                freeSolo
                                options={chronoIdOptions}
                                value={startChrono}
                                onInputChange={(e, v) => setStartChrono(v)}
                                renderInput={(params) => <TextField {...params} label="Chrono (chrono_id)" size="small" />}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                freeSolo
                                options={channelOptionsFor(startChrono)}
                                value={startChannel}
                                onInputChange={(e, v) => setStartChannel(v)}
                                renderInput={(params) => <TextField {...params} label="Canal" size="small" />}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                        <Typography variant="h6">🔵 Intermédiaires (optionnel, jusqu'à 3)</Typography>
                        {intermediates.length < 3 && (
                            <Button size="small" variant="outlined" onClick={addIntermediate}>
                                + Ajouter un intermédiaire
                            </Button>
                        )}
                    </Box>
                    {intermediates.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            Aucun intermédiaire configuré.
                        </Typography>
                    ) : (
                        intermediates.map((it, i) => (
                            <Grid container spacing={2} key={i} sx={{ mb: 1 }} alignItems="center">
                                <Grid item xs={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={chronoIdOptions}
                                        value={it.chronoId}
                                        onInputChange={(e, v) => updateIntermediate(i, "chronoId", v)}
                                        renderInput={(params) => (
                                            <TextField {...params} label={`Chrono — Inter ${i + 1}`} size="small" />
                                        )}
                                    />
                                </Grid>
                                <Grid item xs={4}>
                                    <Autocomplete
                                        freeSolo
                                        options={channelOptionsFor(it.chronoId)}
                                        value={it.channel}
                                        onInputChange={(e, v) => updateIntermediate(i, "channel", v)}
                                        renderInput={(params) => <TextField {...params} label="Canal" size="small" />}
                                    />
                                </Grid>
                                <Grid item xs={3}>
                                    <TextField
                                        label="Distance (m)"
                                        size="small"
                                        type="number"
                                        fullWidth
                                        value={it.distance}
                                        onChange={(e) => updateIntermediate(i, "distance", e.target.value)}
                                    />
                                </Grid>
                                <Grid item xs={1}>
                                    <IconButton color="error" onClick={() => removeIntermediate(i)}>
                                        <DeleteIcon fontSize="small" />
                                    </IconButton>
                                </Grid>
                            </Grid>
                        ))
                    )}
                </Paper>

                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="h6" mb={2}>
                        🏁 Arrivée (obligatoire)
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid item xs={6}>
                            <Autocomplete
                                freeSolo
                                options={chronoIdOptions}
                                value={finishChrono}
                                onInputChange={(e, v) => setFinishChrono(v)}
                                renderInput={(params) => <TextField {...params} label="Chrono (chrono_id)" size="small" />}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                freeSolo
                                options={channelOptionsFor(finishChrono)}
                                value={finishChannel}
                                onInputChange={(e, v) => setFinishChannel(v)}
                                renderInput={(params) => <TextField {...params} label="Canal" size="small" />}
                            />
                        </Grid>
                    </Grid>
                </Paper>

                <Box sx={{ display: "flex", gap: 2 }}>
                    {configured && (
                        <Button variant="outlined" onClick={() => setShowSetup(false)}>
                            Annuler
                        </Button>
                    )}
                    <Button variant="contained" onClick={saveConfig}>
                        {configured ? "Enregistrer" : "Ouvrir la page chrono"}
                    </Button>
                </Box>
            </Box>
        );
    }

    return (
        <Box p={2}>
            <TimingEventSubNav eventKey={eventKey} current="chrono" />
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 1 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
                    <Typography variant="h5">Chrono — {eventKey}</Typography>
                    {isTelemark && <Chip label="Télémark" size="small" color="secondary" />}
                    <Chip
                        label={
                            (liveData.runCount || 1) > 1
                                ? `Manche ${liveData.runNumber || 1} / ${liveData.runCount} — ${
                                    liveData.combineMethod === "best" ? "meilleure manche" : "addition"
                                }`
                                : `Manche ${liveData.runNumber || 1}`
                        }
                        color={liveData.isHistorical ? "warning" : "primary"}
                        size="small"
                    />
                    {[1, 2, 3].map((rn) => (
                        <Chip
                            key={rn}
                            label={`M${rn}`}
                            size="small"
                            clickable
                            variant={(liveData.runNumber || 1) === rn ? "filled" : "outlined"}
                            color={rn > (liveData.currentRun || 1) ? "default" : undefined}
                            onClick={() => switchViewRun(rn)}
                        />
                    ))}
                </Box>
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                        variant="outlined"
                        size="small"
                        color={unresolvedTotalCount > 0 ? "warning" : "primary"}
                        onClick={() => setShowUnresolvedDialog(true)}
                    >
                        ⚠️ À traiter {unresolvedTotalCount > 0 ? `(${unresolvedTotalCount})` : ""}
                    </Button>
                    <Button variant="outlined" size="small" onClick={openRefereeReport}>
                        🖨️ Report by the Referee
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setShareOpen(true)}>
                        📱 PDF / WhatsApp
                    </Button>
                    <Button variant="outlined" size="small" onClick={() => setShowSetup(true)}>
                        Modifier la configuration
                    </Button>
                    {!liveData.isHistorical && (liveData.currentRun || 1) < 3 && (
                        <Button
                            variant="contained"
                            color="success"
                            size="small"
                            disabled={advancingRun}
                            onClick={advanceRun}
                        >
                            {advancingRun
                                ? "Passage en cours…"
                                : `Passer à la manche ${(liveData.currentRun || 1) + 1}`}
                        </Button>
                    )}
                    {!liveData.isHistorical && (
                        <Button
                            variant="contained"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon />}
                            onClick={async () => {
                                if (
                                    !window.confirm(
                                        `Supprimer les impulsions de la manche ${liveData.currentRun || 1} (en cours) ? ` +
                                        `Les manches déjà clôturées ne sont pas touchées. Action irréversible.`
                                    )
                                ) {
                                    return;
                                }
                                if (!window.confirm("Vraiment sûr ? Aucun retour en arrière possible.")) return;

                                const res = await apiFetch(
                                    `/api/timing/clear/${encodeURIComponent(eventKey)}`,
                                    { method: "POST" }
                                );
                                if (res && res.ok) {
                                    loadLive();
                                } else if (res) {
                                    const data = await res.json().catch(() => ({}));
                                    setError(data.error || `HTTP ${res.status}`);
                                }
                            }}
                        >
                            Vider les temps de la manche en cours
                        </Button>
                    )}
                </Box>
            </Box>

            {liveData.isFuture && (
                <Typography
                    variant="body2"
                    sx={{ mb: 2, p: 1.5, bgcolor: "info.light", borderRadius: 1, fontWeight: "bold" }}
                >
                    ℹ La manche {liveData.runNumber} n'a pas encore commencé (la manche en cours est la{" "}
                    {liveData.currentRun}). Cliquez "Passer à la manche suivante" depuis la manche en cours pour
                    l'activer.
                </Typography>
            )}

            {liveData.isHistorical && !liveData.isFuture && (
                <Typography
                    variant="body2"
                    sx={{ mb: 2, p: 1.5, bgcolor: "warning.light", borderRadius: 1, fontWeight: "bold" }}
                >
                    ⚠ Vous consultez la manche {liveData.runNumber} — déjà passée (la manche en cours est
                    la {liveData.currentRun}). Les corrections ici (dossard, annulation, statut) restent
                    possibles et recalculent automatiquement le classement combiné.
                </Typography>
            )}

            {showUnresolvedDialog && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        bgcolor: "rgba(0,0,0,0.5)",
                        zIndex: 1300,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        p: 2,
                    }}
                    onClick={closeUnresolvedDialog}
                >
                    <Paper
                        sx={{ p: 3, maxWidth: 600, width: "100%", maxHeight: "80vh", overflowY: "auto" }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                            <Typography variant="h6">
                                ⚠️ À traiter{!liveData.isHistorical ? " avant de clôturer" : ""} la manche{" "}
                                {liveData.runNumber || 1}
                            </Typography>
                            <Button size="small" onClick={closeUnresolvedDialog}>
                                Fermer
                            </Button>
                        </Box>

                        {unresolvedTotalCount === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                                Rien à traiter — tous les dossards ont un temps ou un statut.
                            </Typography>
                        ) : (
                            <>
                                {unresolvedBibs.length > 0 && (
                                    <Box sx={{ mb: alreadyStatusBibs.length > 0 ? 2 : 0 }}>
                                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                            Sans temps ni statut ({unresolvedBibs.length})
                                        </Typography>
                                        {unresolvedBibs.map((a) => (
                                            <Box
                                                key={a.bib}
                                                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}
                                            >
                                                <Typography>
                                                    [{a.bib}] {a.lastname} {a.firstname}
                                                </Typography>
                                                {renderStatusSelect(a.bib, ["DNS", "DNF", "DSQ", "DSQP"])}
                                            </Box>
                                        ))}
                                    </Box>
                                )}

                                {alreadyStatusBibs.length > 0 && (
                                    <Box>
                                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 0.5 }}>
                                            Déjà un statut ({alreadyStatusBibs.length})
                                        </Typography>
                                        {alreadyStatusBibs.map((n) => (
                                            <Box
                                                key={n.bib}
                                                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", py: 0.5 }}
                                            >
                                                <Typography>
                                                    [{n.bib}] {n.name || "—"}
                                                    {(n.status === "DSQ" || n.status === "DSQP") && n.gateNumber ? ` — porte ${n.gateNumber}` : ""}
                                                </Typography>
                                                {renderStatusSelect(n.bib, ["DNS", "DNF", "DSQ", "DSQP"])}
                                            </Box>
                                        ))}
                                    </Box>
                                )}
                            </>
                        )}
                    </Paper>
                </Box>
            )}

            <Dialog open={shareOpen} onClose={() => setShareOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle>PDF officiels / WhatsApp</DialogTitle>
                <DialogContent>
                    {shareError && <Alert severity="error" sx={{ mb: 1 }}>{shareError}</Alert>}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        Documents à envoyer (manche {liveData.runNumber || 1}) :
                    </Typography>
                    {[
                        ["startlist", `Liste de départ — manche ${liveData.runNumber || 1}`],
                        ["run", `Résultats — manche ${liveData.runNumber || 1}`],
                        ["event", (liveData.runCount || 1) > 1 ? "Résultats de l'épreuve (total)" : "Résultats de l'épreuve"],
                        ["analysis", `Analyse — manche ${liveData.runNumber || 1} (secteurs)`],
                        ["referee", "Report by the Referee"],
                        ["text", "Classement en texte dans le message"],
                    ].map(([k, label]) => (
                        <FormControlLabel
                            key={k}
                            sx={{ display: "block" }}
                            control={<Checkbox checked={shareSel[k]} onChange={(e) => setShareSel((p) => ({ ...p, [k]: e.target.checked }))} />}
                            label={label}
                        />
                    ))}
                    <Divider sx={{ my: 1 }} />
                    <FormControlLabel
                        sx={{ display: "block" }}
                        control={<Checkbox checked={shareSorted} onChange={(e) => setShareSorted(e.target.checked)} />}
                        label={raceConfig?.printSort?.sections?.length ? "Appliquer le Tri impression (sections)" : "Grouper par catégorie"}
                    />
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, ml: 4, mb: 1, flexWrap: "wrap" }}>
                        <Typography variant="caption" color="text.secondary">
                            {raceConfig?.printSort?.sections?.length
                                ? `Sections : ${groupBySections(startlist, raceConfig.printSort, raceConfig.categories)
                                    .map((g) => `${g.category} (${g.entries.length})`)
                                    .join(" → ")}`
                                : "Aucun tri défini."}
                        </Typography>
                        <Button size="small" onClick={() => setShowPrintSort(true)}>
                            Modifier le Tri impression
                        </Button>
                    </Box>
                    <FormControlLabel
                        sx={{ display: "block" }}
                        control={<Checkbox checked={shareSheet} onChange={(e) => setShareSheet(e.target.checked)} />}
                        label="Inclure la fiche de course (jury, parcours)"
                    />
                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Téléphone : tous les PDF partent ensemble dans WhatsApp. Ordinateur : les PDF sont téléchargés et
                        WhatsApp Web s'ouvre — joindre les fichiers à la main.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShareOpen(false)}>Annuler</Button>
                    <Button variant="contained" onClick={shareSelected} disabled={shareBusy || !Object.values(shareSel).some(Boolean)}>
                        {shareBusy ? "Préparation…" : "Partager"}
                    </Button>
                </DialogActions>
            </Dialog>

            <PrintSortManager
                open={showPrintSort}
                onClose={() => setShowPrintSort(false)}
                eventKey={eventKey}
                startlist={startlist}
                onSaved={(ps) => {
                    setRaceConfig((prev) => ({ ...(prev || {}), printSort: ps }));
                    setShareSorted(true);
                }}
            />

            <FisStatusMini eventKey={eventKey} run={liveData.runNumber || 1} />

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, gap: 2, mb: 2, alignItems: "start" }}>
                <Box>
                    {next5.length > 0 && (
                        <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                Prochains au départ
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                Le prochain à partir est en bas.
                            </Typography>
                            {[...next5].reverse().map((a, displayIndex) => {
                                const realIndex = next5.length - 1 - displayIndex;

                                return (
                                    <Box
                                        key={`${a.fiscode}-${realIndex}`}
                                        sx={{
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "space-between",
                                            py: 0.5,
                                        }}
                                    >
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            <Typography sx={{ minWidth: 20 }}>{realIndex + 1}.</Typography>
                                            <TextField
                                                size="small"
                                                defaultValue={a.bib ?? ""}
                                                onKeyDown={(e) => {
                                                    if (e.key === "Enter") e.target.blur();
                                                }}
                                                onBlur={(e) => {
                                                    const value = e.target.value;
                                                    if (value !== String(a.bib ?? "")) saveStartlistBib(a, value);
                                                }}
                                                sx={{ width: 70 }}
                                            />
                                            <Typography>
                                                {a.lastname} {a.firstname}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                            {renderStatusSelect(a.bib, ["DNS"])}
                                            <IconButton
                                                size="small"
                                                onClick={() => moveNext5(realIndex, 1)}
                                                disabled={realIndex === next5.length - 1}
                                            >
                                                <ArrowUpwardIcon fontSize="small" />
                                            </IconButton>
                                            <IconButton
                                                size="small"
                                                onClick={() => moveNext5(realIndex, -1)}
                                                disabled={realIndex === 0}
                                            >
                                                <ArrowDownwardIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Paper>
                    )}
                </Box>

                {/* Une fenêtre "en attente de ..." par étape (inter1, inter2,
                    inter3, arrivée) — ajouté le 06.09.2026. Cet ordre est ce qui
                    détermine l'assignation "auto" du prochain dossard reçu à
                    CETTE étape (voir mergeStageQueue côté serveur). Un statut
                    donné ici sort immédiatement le coureur de la file. */}
                {(liveData.stageQueues || []).map((sq) => (
                    <Box key={sq.key}>
                        <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
                            <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                                {sq.key === "finish" ? "🏁" : "🔵"} En attente — {sq.label}
                                {sq.distance ? ` (${sq.distance}m)` : ""}
                            </Typography>
                            {sq.waiting.length > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
                                    Le prochain attendu ici est en bas.
                                </Typography>
                            )}
                            {sq.waiting.length === 0 ? (
                                <Typography color="text.secondary" variant="body2">
                                    Personne en attente actuellement.
                                </Typography>
                            ) : (
                                [...sq.waiting].reverse().map((entry, displayIndex) => {
                                    const realIndex = sq.waiting.length - 1 - displayIndex;
                                    const elapsed = entry.sinceTime
                                        ? now - new Date(entry.sinceTime).getTime()
                                        : null;
                                    return (
                                        <Box key={`${sq.key}-${entry.bib}-${realIndex}`}>
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    justifyContent: "space-between",
                                                    py: 0.5,
                                                }}
                                            >
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                    <Typography sx={{ minWidth: 18 }}>{realIndex + 1}.</Typography>
                                                    <TextField
                                                        size="small"
                                                        defaultValue={entry.bib ?? ""}
                                                        onKeyDown={(e) => {
                                                            if (e.key === "Enter") e.target.blur();
                                                        }}
                                                        onBlur={(e) => {
                                                            const value = e.target.value;
                                                            if (value !== String(entry.bib ?? ""))
                                                                editStageQueueBib(sq.key, realIndex, value);
                                                        }}
                                                        sx={{ width: 55 }}
                                                    />
                                                    <Box>
                                                        <Typography variant="body2">{entry.name || "—"}</Typography>
                                                        {elapsed != null && (
                                                            <Typography
                                                                sx={{
                                                                    fontFamily: "monospace",
                                                                    fontSize: "0.85rem",
                                                                    fontWeight: "bold",
                                                                    color: "primary.main",
                                                                }}
                                                            >
                                                                {formatElapsed(elapsed)}
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                </Box>
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
                                                    {renderStatusSelect(entry.bib)}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => moveStageQueueItem(sq.key, realIndex, 1)}
                                                        disabled={realIndex === sq.waiting.length - 1}
                                                    >
                                                        <ArrowUpwardIcon fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => moveStageQueueItem(sq.key, realIndex, -1)}
                                                        disabled={realIndex === 0}
                                                    >
                                                        <ArrowDownwardIcon fontSize="small" />
                                                    </IconButton>
                                                </Box>
                                            </Box>
                                            {displayIndex < sq.waiting.length - 1 && <Divider />}
                                        </Box>
                                    );
                                })
                            )}
                        </Paper>
                    </Box>
                ))}
            </Box>

            {/* Rangée des impulsions brutes par canal — MÊME grille 4
                colonnes et MÊME ordre (Départ → Inter 1..3 → Arrivée) que
                la rangée ci-dessus. */}
            <Box sx={{ display: "grid", gridTemplateColumns: GRID_COLUMNS, gap: 2, mb: 2 }}>
                <Box>
                    {renderImpulseColumn(
                        `🟢 Départ (M${liveData.runNumber || 1})`,
                        liveData.start,
                        startEnabled,
                        setStartEnabled,
                        "start"
                    )}
                </Box>
                {intermediates.map((it, i) => (
                    <Box key={i}>
                        {renderImpulseColumn(
                            `🔵 Inter ${i + 1} (M${liveData.runNumber || 1})${it.distance ? ` — ${it.distance}m` : ""}`,
                            liveData.intermediates?.[i] || [],
                            interEnabled[i] ?? true,
                            (val) => setInterEnabled((prev) => prev.map((v, idx) => (idx === i ? val : v))),
                            `inter${i + 1}`
                        )}
                    </Box>
                ))}
                <Box>
                    {renderImpulseColumn(
                        `🏁 Arrivée (M${liveData.runNumber || 1})`,
                        liveData.finish,
                        finishEnabled,
                        setFinishEnabled,
                        "finish"
                    )}
                </Box>
            </Box>

            {/* Course télémark : classement avec temps ski / pénalités
                (style + jump) / temps manche, et classement combiné
                pénalités incluses — voir TelemarkRanking.jsx. Sinon,
                classements habituels ci-dessous. */}
            {isTelemark ? (
                <TelemarkRanking
                    eventKey={eventKey}
                    viewRun={liveData.runNumber || Number(searchParams.get("run")) || null}
                    runCount={liveData.runCount || 1}
                    combineMethod={liveData.combineMethod}
                    renderStatusSelect={renderStatusSelect}
                />
            ) : (
                <>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Typography variant="h6" mb={1}>
                            🏆 Classement {(liveData.runCount || 1) > 1 ? `— Manche ${liveData.runNumber || 1}` : ""}
                        </Typography>
                        {liveData.ranking.length === 0 ? (
                            <Typography color="text.secondary" variant="body2">
                                Aucun temps calculé pour l'instant.
                            </Typography>
                        ) : (
                            <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                                {liveData.ranking.map((r, i) => (
                                    <Box key={r.bib + "-" + i}>
                                        <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, px: 1 }}>
                                            <Box>
                                                <Typography sx={{ fontWeight: "bold" }}>
                                                    {r.rank}. [{r.bib}] {r.name || "—"}
                                                </Typography>
                                                {r.nation && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {r.nation}
                                                    </Typography>
                                                )}
                                            </Box>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                                                {renderStatusSelect(r.bib)}
                                                <Typography sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                                                    {formatElapsed(r.elapsedMs)}
                                                </Typography>
                                            </Box>
                                        </Box>
                                        {i < liveData.ranking.length - 1 && <Divider />}
                                    </Box>
                                ))}
                            </Box>
                        )}

                        {liveData.notClassified?.length > 0 && (
                            <Box sx={{ mt: 2, pt: 2, borderTop: "1px solid #ddd" }}>
                                <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                                    Non classés
                                </Typography>
                                {liveData.notClassified.map((n, i) => (
                                    <Box
                                        key={n.bib + "-" + i}
                                        sx={{ display: "flex", justifyContent: "space-between", py: 0.5, px: 1 }}
                                    >
                                        <Typography>
                                            [{n.bib}] {n.name || "—"}
                                            {(n.status === "DSQ" || n.status === "DSQP") && n.gateNumber ? ` — porte ${n.gateNumber}` : ""}
                                        </Typography>
                                        <Chip label={n.status} size="small" color="error" variant="outlined" />
                                    </Box>
                                ))}
                            </Box>
                        )}
                    </Paper>

                    {liveData.combinedRanking && (
                        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
                            <Typography variant="h6" mb={1}>
                                🏆 Classement combiné —{" "}
                                {liveData.combineMethod === "best" ? "meilleure manche" : "addition des manches"} (
                                {Math.min(liveData.currentRun || 1, liveData.runCount || 1)} / {liveData.runCount} disputée
                                {Math.min(liveData.currentRun || 1, liveData.runCount || 1) > 1 ? "s" : ""})
                            </Typography>
                            {liveData.combinedRanking.length === 0 ? (
                                <Typography color="text.secondary" variant="body2">
                                    Personne classé sur l'ensemble des manches pour l'instant.
                                </Typography>
                            ) : (
                                <Box sx={{ maxHeight: 600, overflowY: "auto" }}>
                                    {liveData.combinedRanking.map((r, i) => (
                                        <Box key={r.bib + "-" + i}>
                                            <Box sx={{ display: "flex", justifyContent: "space-between", py: 0.75, px: 1 }}>
                                                <Box>
                                                    <Typography sx={{ fontWeight: "bold" }}>
                                                        {r.rank}. [{r.bib}] {r.name || "—"}
                                                    </Typography>
                                                    {r.nation && (
                                                        <Typography variant="caption" color="text.secondary">
                                                            {r.nation}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                                                        {Object.entries(r.runs)
                                                            .sort(([a], [b]) => Number(a) - Number(b))
                                                            .map(([rn, entry]) =>
                                                                entry.elapsedMs != null
                                                                    ? `M${rn}: ${formatElapsed(entry.elapsedMs)}`
                                                                    : `M${rn}: ${entry.status || "—"}`
                                                            )
                                                            .join("  ·  ")}
                                                    </Typography>
                                                </Box>
                                                <Typography sx={{ fontFamily: "monospace", fontWeight: "bold" }}>
                                                    {formatElapsed(r.totalMs)}
                                                </Typography>
                                            </Box>
                                            {i < liveData.combinedRanking.length - 1 && <Divider />}
                                        </Box>
                                    ))}
                                </Box>
                            )}
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
}